import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/button';
import {
  classifyGoogleAdsCallback,
  parseGoogleAdsCallbackParams,
} from '../../lib/googleAdsConnect';
import { GoogleAdsAccountPicker } from './GoogleAdsAccountPicker';

// Per-account Google Ads connect callback. Google Ads is a standalone OAuth in
// Zernio, and connectAds auto-binds the first customer the token sees — so this
// page HOSTS the picker (enumerate → pick → bind), mirroring the GBP callback's
// location picker. The user consents to Google, lands here, then chooses the
// correct customer.
//
// ⚠️ redirect_url is documented "same-token platforms only" and googleads is
// standalone, so Zernio MAY ignore it and land the browser elsewhere (with no
// ?kyckAccountId). If so, this page shows a "can't resolve the account" note and
// points back to the account's Connections tab — where the "Change" button opens
// the SAME picker with no re-OAuth. See googleAdsConnect.ts for the flag.

export function GoogleAdsCallbackPage() {
  const [params] = useSearchParams();
  const parsed = useMemo(() => parseGoogleAdsCallbackParams(params), [params]);
  const outcome = classifyGoogleAdsCallback(parsed);
  const accountId = parsed.kyckAccountId ?? '';
  const [done, setDone] = useState(false);

  return (
    <div className="mx-auto max-w-xl">
      <PageHeader
        title="Connect Google Ads"
        description="Choose the Google Ads account for this client."
        backHref={accountId ? `/accounts/${accountId}?tab=connections` : '/accounts'}
      />

      {outcome === 'error' ? (
        <p className="text-sm text-rose-600">
          Google Ads couldn't be connected ({parsed.error}). Restart the connect flow from the
          account's API Connections section.
        </p>
      ) : outcome === 'missing-account' ? (
        <p className="text-sm text-slate-600">
          Google Ads was authorised, but we couldn't tell which account to bind it to from this
          redirect. Open the account's <span className="font-medium">API Connections</span> section
          and use <span className="font-medium">Change Google Ads account</span> to pick the right
          customer.
        </p>
      ) : done ? (
        <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-4">
          <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
          <div className="text-sm text-emerald-900">
            <p className="font-semibold">Google Ads account bound</p>
            <p className="mt-0.5">The Google Ads customer is now linked to this account.</p>
            <Button asChild size="sm" className="mt-3">
              <Link to={`/accounts/${accountId}?tab=connections`}>Back to the account</Link>
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Google Ads is authorised. Choose the account this client should use — check the currency
            matches the client's country.
          </p>
          <GoogleAdsAccountPicker accountId={accountId} onDone={() => setDone(true)} />
        </div>
      )}
    </div>
  );
}
